import { useGo, useTranslate } from "@refinedev/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBoundStore } from "@/store";
import {
  Button,
  Flex,
  Space,
  notification,
  Tabs,
  Popconfirm,
  Drawer,
  Tooltip,
  Typography,
  Skeleton,
  Tag,
  ConfigProvider,
} from "antd";
import { NewHighlight } from "react-pdf-highlighter";
import { JsonPreview } from "../JsonPreview";
import {
  ChapterCuePointsResponse,
  LegacyHighlightsConversionResponse,
  StoryboardHighlights,
  Storyboard,
} from "pages/media/types";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { useParams } from "react-router-dom";
import { transformKeysToCamelCase } from "pages/media/utils";
import { TimelineState } from "@xzdarcy/react-timeline-editor";
import { useAntTheme } from "hooks/useAntTheme";
import { usePagePadding } from "components/layout/pagePadding";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { StyledVerticalPanelResizeHandle } from "components/StyledPanelResizeHandle";
import {
  ArrowSquareUpRightIcon,
  BugBeetleIcon,
  InfoIcon,
  PlayIcon,
} from "@phosphor-icons/react";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { SubPageHeader } from "components/SubPageHeader";
import { useCuepointsStore } from "../animator/useCuepointsStore";
import {
  HighlightType,
  NewHtmlHighlight,
  NewPdfHighlight,
  PdfHighlight,
  useHighlightsStore,
} from "../animator/useHighlightsStore";
import { AnimationVideo } from "../animator/AnimationVideo";
import {
  defaultRoughNotation,
  extractSourcesHighlightsCuepoints,
  loadProjectAsset,
} from "./helpers/helpers";
import { LiveHintWindow } from "./components/LiveHintWindow";
import { AnimatorScript } from "./components/AnimatorScript";
import { STORAGE_SETTINGS_KEY } from "../animator/constants";
import { useAnimation } from "./useAnimation";
import { AnimationWeb } from "../animator/AnimationWeb";
import { ExtendedSectionHighlight } from "./types";
import { v4 as uuidv4 } from "uuid";
import { Cuepoint } from "../binder/types";
import { postToIframe } from "../animator/utils";
import { TimelineComponent } from "../animator/Timeline";
import { PdfHighlightsViewer } from "components/PdfHighlightsViewer";
import { useLegacyAnimation } from "./useLegacyAnimation";
import { InspectorToolbar } from "../animator/InspectorToolbar";
import { LoadingSpinner } from "components/LoadingSpinner";
import { NoAnimationModal } from "./components/NoAnimationModal";
import { produce } from "immer";
import useIsAdmin from "hooks/useIsAdmin/";
import { TrackedButton } from "components/TrackedButton/";
import { ProjectType } from "../../projects/types";
import { getDirectionStyle, isRTL } from "helpers/utils/";
import { useModifyColorAllHighlights } from "./hooks/highlights/useModifyColorAllHighlights";
import { useRemoveAll } from "./hooks/shared/useRemoveAll";
import { useFetchChaptersWithCuepoints } from "./helpers/shared/fetchChaptersWithCuepoints";
import { useDisplayModalConfirm } from "pages/media/components/animation/hooks/shared/useDisplayModalConfirm/";
import useNavigationBlocker from "./hooks/navigation/useNavigationBlocker";
import { useSaveAnimation } from "./hooks/highlights/useSaveAnimation";
import { ColorPicker } from "components/ColorPicker/ColorPicker";
import { getProjectDetailRoute } from "pages/media/projects/helpers/helper";

export const Animation = () => {
  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );
  const [ts, setTs] = useState(0); // in milliseconds to save precision for the words timestamps
  const go = useGo();
  const t = useTranslate();
  const { trackEvent } = useAnalytics();
  const isAdmin = useIsAdmin();
  const {
    cuepoints,
    setCuepoints,
    setUpdateCount,
    currentCuepoint,
    setCurrentCuepoint,
    addUpdateCount,
    updateCount,
    resetCuepointsStore,
  } = useCuepointsStore();

  const {
    setCurrentHighlight,
    addHighlight,
    currentHighlight,
    setHighlights,
    highlights,
    setSectionHighlights,
    sectionHighlights,
    highlightedStoryboard,
    setHighlightedStoryboard,
    resetHighlightsStore,
  } = useHighlightsStore();

  const [newSectionHighlight, setNewSectionHighlight] =
    useState<ExtendedSectionHighlight | null>(null);

  const [storyboardHighlights, setStoryboardHighlights] =
    useState<StoryboardHighlights>(null);

  const { disableBlocking } = useNavigationBlocker(updateCount);

  const [scrollTo, setScrollTo] = useState("");
  const [api, contextHolder] = notification.useNotification();
  const { theme } = useAntTheme();
  // Edge-to-edge warning bar: cancels the app shell's horizontal page padding.
  const pagePadding = usePagePadding();
  const timelineRef = useRef<{
    container: HTMLDivElement;
    player: TimelineState;
    scaleWidth: number;
  }>(null);
  const [duration, setDuration] = useState(100);
  const [saving, setSaving] = useState(false);
  const params = useParams<{ id: string; mediaId: string }>(); // Add mediaId to the type
  const { id: projectId, mediaId } = params ?? {};
  const [debug, setDebug] = useState(false);
  // This should never change in the PDF editor!
  const [viewType, setViewType] = useState<ViewType>("editor");

  const {
    organizationId,
    highlightsAssetPath,
    isLoadingProject,
    projectData,
    media,
    documentAsset,
    brandKit,
    timingChapters,
    hasUploadedVideo,
    documentId,
  } = useAnimation(projectId, mediaId);

  const modifyColorAllHighlights = useModifyColorAllHighlights();
  const removeAll = useRemoveAll();
  const fetchChaptersWithCuepoints =
    useFetchChaptersWithCuepoints(timingChapters);
  const { displayModalConfirm } = useDisplayModalConfirm();

  const {
    refetchMediaAssets,
    refetchProjectAssets,
    projectAssets,
    mediaAssets,
    isLoadingMediaAssets,
    isLoadingProjectAssets,
    filterAssets,
  } = useMediaAssetsStorage({
    organizationId: organizationId,
    projectId,
    mediaId,
    enabled: Boolean(projectId && mediaId),
    invalidateOnMutationSuccess: false,
  });

  const isLoading =
    isLoadingMediaAssets || isLoadingProjectAssets || isLoadingProject;
  const hasLegacyDataToMigrate =
    !isLoading &&
    storyboardHighlights &&
    !storyboardHighlights.section_highlights;

  useEffect(() => {
    return () => {
      resetHighlightsStore();
      resetCuepointsStore();
    };
  }, [resetHighlightsStore, resetCuepointsStore]);

  const storyboard = projectData?.data?.storyboards[0];
  const storyboardId = storyboard?.asset_id;
  const storyboardAsset = projectAssets?.find(
    (asset) => asset.asset_type === "Storyboard" && asset.id === storyboardId
  );
  const storyboardAssetPath = storyboardAsset?.path;
  const storyboardLanguage = storyboard?.language;
  const layoutDirection = getDirectionStyle(storyboardLanguage);

  const saveUpdatedData = useSaveAnimation(
    projectId,
    mediaId,
    storyboardId,
    setNewSectionHighlight
  );

  const cuepointsAsset = mediaAssets
    ? filterAssets(mediaAssets, {
        asset_type: "CuePoints",
      })?.[0]
    : null;

  const cuepointsAssetPath = cuepointsAsset?.path;

  const correctCuepoints = useCallback(
    async (
      storyboardHighlights: StoryboardHighlights,
      storyboard: Storyboard
    ) => {
      if (hasUploadedVideo || !storyboard) return;
      console.debug("Correcting cuepoints...");
      try {
        const backendChapters = await fetchChaptersWithCuepoints(storyboard);
        const updatedStoryboardHighlight = { ...storyboardHighlights };
        for (const [key, values] of Object.entries(
          storyboardHighlights?.section_map
        )) {
          const currentChapter = backendChapters?.find(
            (c) => c.chapter_name === values
          );

          updatedStoryboardHighlight.section_highlights[key].cuepoints =
            currentChapter?.cuepoints?.cuepoints ?? [];
        }

        return updatedStoryboardHighlight;
      } catch (error) {
        console.error("Error correcting cuepoints:", error);
        return;
      }
    },
    [fetchChaptersWithCuepoints, hasUploadedVideo]
  );

  const handleLegacyDataMigrationSuccess = async (
    data: LegacyHighlightsConversionResponse
  ) => {
    const { highlights, storyboard } = data;
    setHighlightedStoryboard(storyboard);
    try {
      if (hasUploadedVideo) {
        const updatedHighlights = produce(highlights, (draft) => {
          for (const cuepoint of draft.section_highlights.no_storyboard
            .cuepoints) {
            cuepoint.duration = 1000;
          }
        });
        setStoryboardHighlights(updatedHighlights);
      } else {
        let storyboardHighlights = highlights;
        const storyboardHighlightsWithCorrectedCuepoints =
          await correctCuepoints(storyboardHighlights, storyboard);
        if (storyboardHighlightsWithCorrectedCuepoints) {
          storyboardHighlights = storyboardHighlightsWithCorrectedCuepoints;
        }
        setStoryboardHighlights(storyboardHighlights);
      }
      addUpdateCount();
    } catch (error) {
      // something went wrong, the user will still see the Loading spinner
      console.error("Error while migrating data:", error);
    }
  };

  const {
    migrate: migrateLegacyData,
    isLoading: isLegacyDataMigrationLoading,
    isError: isLegacyDataMigrationError,
    isSuccess: migrationCompleted,
  } = useLegacyAnimation(
    documentAsset?.id,
    handleLegacyDataMigrationSuccess,
    {
      highlightsAssetPath,
      cuepointsAssetPath,
      storyboardAssetPath,
    },
    hasUploadedVideo
  );

  const isPendingMigration = hasLegacyDataToMigrate && !migrationCompleted;
  const isScriptLoading = isLoading || isPendingMigration;

  const storyboardHighlightsProcessedRef = useRef(false);

  const processStoryboardHighlights = useCallback(async () => {
    try {
      if (
        // States are loaded asynchronously. We need both to correct the cuepoints.
        !storyboardHighlights?.section_highlights ||
        !highlightedStoryboardRef.current
      ) {
        return;
      }

      let storyboardHighlightsWithCorrectCuepoints = storyboardHighlights;

      const hasCuepointsToCorrect = Object.values(
        storyboardHighlights.section_highlights
      ).some((v) => {
        const noDuration = v.cuepoints.some((c) => c.duration === -1);
        const noCuepoints =
          v.highlights?.length > 0 && v.cuepoints?.length === 0;
        return noDuration || noCuepoints;
      });

      if (hasCuepointsToCorrect) {
        const updatedStoryboardHighlights = await correctCuepoints(
          storyboardHighlights,
          highlightedStoryboardRef.current
        );
        if (updatedStoryboardHighlights) {
          storyboardHighlightsWithCorrectCuepoints =
            updatedStoryboardHighlights;
        }
      }

      const { sources, highlights, cuepoints } =
        extractSourcesHighlightsCuepoints(
          storyboardHighlightsWithCorrectCuepoints
        );

      setHighlights(highlights);
      setCuepoints(cuepoints);
      setSectionHighlights(sources);
      storyboardHighlightsProcessedRef.current = true;
    } catch (error) {
      console.error("Error processing highlights:", error);
    }
  }, [
    correctCuepoints,
    setCuepoints,
    setHighlights,
    setSectionHighlights,
    storyboardHighlights,
  ]);

  //A workaround to avoid calling processStoryboardHighlights() on each change to the highlightedStoryboard.
  const highlightedStoryboardRef = useRef(highlightedStoryboard);

  useEffect(() => {
    highlightedStoryboardRef.current = highlightedStoryboard;
    //called when storyboardHighlights is changed, thanks to the processStoryboardHighlights
    // dependency
    // we force the processing of the highlights the first time the highlightedStoryboardRef
    // is set with the fresh data
    // processStoryboardHighlights might be called too early with empty highlightedStoryboardRef.current
    // we removed storyboardHighlights from the dependency array of processStoryboardHighlights
    // for performance reasons
    if (!storyboardHighlightsProcessedRef.current) {
      processStoryboardHighlights().catch((error) =>
        console.error("Error processing storyboard highlights", error)
      );
    }
  }, [highlightedStoryboard, processStoryboardHighlights]);

  useEffect(() => {
    if (hasLegacyDataToMigrate) {
      migrateLegacyData().catch((error) =>
        console.error("Error migrating data", error)
      );
    }
  }, [
    isLoading,
    storyboardHighlights,
    migrateLegacyData,
    hasLegacyDataToMigrate,
  ]);

  useEffect(() => {
    //highlightsAssetPath may contain either the new section_highlights data or the old highlights array data,
    //if we have legacy data to migrate we shouldn't load old HLs
    if (!highlightsAssetPath || isPendingMigration) return;
    const loadHighlights = async () => {
      const data = await loadProjectAsset(highlightsAssetPath);
      if (data) {
        setStoryboardHighlights(data);
      }
    };
    // if no legacy data to migrate we can load HL from the path
    if (!migrationCompleted) {
      loadHighlights().catch((error) =>
        console.error("Error loading highlights", error)
      );
    }
  }, [highlightsAssetPath, migrationCompleted, isPendingMigration]);

  useEffect(() => {
    const loadStoryboard = async () => {
      const data = await loadProjectAsset(storyboardAssetPath);
      if (data) {
        setHighlightedStoryboard(data);
      }
    };

    //if we have data to migrate we load legacy storyboard
    // from useLegacyAnimation during data migration flow
    // WARNING : the storyboard will not be loaded at all for a video only project
    // requiring migration, and the highlightedStoryboard will stay null
    if (!storyboardAssetPath || isPendingMigration) return;

    // if no legacy data to migrate we can load storyboard
    if (!migrationCompleted && storyboardAssetPath) {
      loadStoryboard().catch((error) =>
        console.error("Error loading storyboard", error)
      );
    }
  }, [
    storyboardAssetPath,
    setHighlightedStoryboard,
    migrationCompleted,
    isPendingMigration,
  ]);

  useEffect(() => {
    setUpdateCount(0);
  }, [setUpdateCount]);

  const playerCurrentTime = timelineRef.current?.player.getTime();
  // console.debug("Animation playerCurrentTime", playerCurrentTime);
  //Scroll timeline when cursor reaches page width
  useEffect(() => {
    if (
      timelineRef.current?.container &&
      timelineRef.current?.player &&
      timelineRef.current?.scaleWidth
    ) {
      const containerWidth = timelineRef.current.container.clientWidth;
      const cursorPosition =
        playerCurrentTime * timelineRef.current?.scaleWidth; //px

      const timelineScrollPage = Math.floor(cursorPosition / containerWidth);
      const newScrollPosition = timelineScrollPage * containerWidth;

      const currentScrollLeft =
        timelineRef.current.player.target.scrollLeft ?? 0;
      if (newScrollPosition - currentScrollLeft > 20) {
        timelineRef.current.player.setScrollLeft(newScrollPosition);
      }
    }
  }, [playerCurrentTime]);

  useEffect(() => {
    const checkVideoReady = () => {
      const videoElement = document.querySelector("video");

      if (videoElement && videoElement.readyState >= 1) {
        console.debug("video is ready", {
          duration: videoElement.duration,
          currentTime: videoElement.currentTime,
        });

        const handleTimeUpdate = () => {
          const videoCurrentTime = videoElement.currentTime;
          timelineRef.current?.player.setTime(videoCurrentTime);

          if (!videoElement.paused) {
            setTs(videoCurrentTime * 1000);
          }
        };

        videoElement.addEventListener("timeupdate", handleTimeUpdate);

        timelineRef.current?.player.setPlayRate(videoElement.duration);
        setDuration(videoElement.duration);
        clearInterval(intervalId);
      }
    };
    const intervalId = setInterval(checkVideoReady, 100);
  }, [highlights]);

  const handleDragEnd = (time: number) => {
    const videoElement = document.querySelector("video");
    if (videoElement) {
      videoElement.currentTime = time;
    }
    setTs(time * 1000);
  };

  const handleDragStart = () => {
    const videoElement = document.querySelector("video");
    if (videoElement) {
      videoElement.pause();
    }
  };

  const updateCuepointDuration = async (
    sectionId: string,
    updatedStoryboard: Storyboard
  ) => {
    const backendChapters = await fetchChaptersWithCuepoints(updatedStoryboard);
    const chapterName = storyboardHighlights?.section_map[sectionId];
    const currentChapter = backendChapters?.find(
      (c) => c.chapter_name === chapterName
    );

    if (currentChapter) {
      const newCuepoints = cuepoints.map((c) => {
        const pickedCuepoint = currentChapter?.cuepoints?.cuepoints?.find(
          (cuepoint) => cuepoint.highlight_id === c.highlightId
        );
        if (pickedCuepoint) {
          return {
            ...c,
            start: pickedCuepoint.start / 1000,
            duration: pickedCuepoint.duration / 1000,
          };
        }
        return c;
      });

      setCuepoints(newCuepoints);
    }
  };

  const roughNotation = useMemo(
    () => ({
      ...defaultRoughNotation,
      color:
        (brandKit?.data?.palette.highlight_color ||
          brandKit?.data?.palette.primary_color) ??
        defaultRoughNotation.color,
    }),
    [
      brandKit?.data?.palette.highlight_color,
      brandKit?.data?.palette.primary_color,
    ]
  );

  const addNewCuepointToLocal = useCallback(
    (cuepointsChapters: ChapterCuePointsResponse[]): Cuepoint => {
      const updatedCuepoints = [];
      for (const chapter of cuepointsChapters) {
        updatedCuepoints.push(...chapter.cuepoints.cuepoints);
      }
      const newCuepointIndex = updatedCuepoints.findIndex(
        (cuepoint) => cuepoint.highlight_id === newSectionHighlight.id
      );
      if (newCuepointIndex === -1) return;

      const cue = updatedCuepoints[newCuepointIndex];

      const updatedCuepoint = {
        ...transformKeysToCamelCase(cue),
        start: cue?.start / 1000,
        duration: cue?.duration / 1000,
        sourceId: newSectionHighlight.sourceId,
        roughNotation,
      };

      setCuepoints([...cuepoints, updatedCuepoint]);
      return updatedCuepoint;
    },
    [
      cuepoints,
      newSectionHighlight?.id,
      newSectionHighlight?.sourceId,
      roughNotation,
      setCuepoints,
    ]
  );

  const handleAddHighlightOnlyVideo = useCallback(
    (highlight: NewHighlight) => {
      const currentTime = Math.round(ts / 1000);

      const currentTimeAlreadyUsed = cuepoints.find(
        (cuepoint) => Math.round(cuepoint.start) === currentTime
      );

      if (currentTimeAlreadyUsed) {
        api.error({
          message: t(`components.binder.index.startTimeInUse`),
          description: t("components.binder.index.youAreTrying"),
          placement: "top",
        });
        return;
      }

      const newHighlight = {
        ...highlight,
        id: uuidv4(),
        type: "pdf" as HighlightType,
        roughNotation,
        generated: false,
      };

      const newSectionHighlight: ExtendedSectionHighlight = {
        id: uuidv4(),
        text: "",
        sourceId: newHighlight.id,
        rank: 0,
        generated: false,
        sectionId: "no_storyboard",
      };

      const newCuepoint: Cuepoint = {
        highlightId: newSectionHighlight.id,
        start: ts / 1000,
        duration: 1,
        sourceId: newHighlight.id,
        documentId,
        roughNotation,
      };

      addHighlight(newHighlight);
      setCurrentHighlight(newHighlight);
      setScrollTo(newHighlight.id);
      setSectionHighlights([...sectionHighlights, newSectionHighlight]);
      setCuepoints([...cuepoints, newCuepoint]);

      timelineRef.current?.player?.setTime(newCuepoint.start);

      addUpdateCount();
      setNewSectionHighlight(undefined);
    },
    [
      ts,
      cuepoints,
      roughNotation,
      documentId,
      sectionHighlights,
      addHighlight,
      setCurrentHighlight,
      setSectionHighlights,
      setCuepoints,
      api,
      t,
      addUpdateCount,
      setNewSectionHighlight,
    ]
  );

  const handleAddHighlight = useCallback(
    async (
      highlight: NewPdfHighlight | NewHtmlHighlight,
      type: HighlightType = "pdf"
    ) => {
      trackEvent(AnalyticsEvents.DocumentAnimationHighlightAdd, {
        project_id: projectId,
        highlight_type: currentCuepoint?.roughNotation?.type,
        highlight_source: "user",
        document_format: type,
      });

      if (hasUploadedVideo && "position" in highlight) {
        handleAddHighlightOnlyVideo(highlight);
        return;
      }
      if (!newSectionHighlight) return;
      const newHighlight = {
        ...highlight,
        id: newSectionHighlight.sourceId,
        type,
      };

      addHighlight(newHighlight);
      setCurrentHighlight(newHighlight);
      setScrollTo(newHighlight.id);

      const backendChapters = await fetchChaptersWithCuepoints(
        highlightedStoryboard
      );
      const newCuepoint = addNewCuepointToLocal(backendChapters);
      setCurrentCuepoint(newHighlight.id);

      timelineRef.current?.player?.setTime(newCuepoint.start);

      addUpdateCount();
      setNewSectionHighlight(undefined);
      return { highlight: newHighlight, cuepoint: newCuepoint };
    },
    [
      addHighlight,
      addNewCuepointToLocal,
      currentCuepoint?.roughNotation?.type,
      fetchChaptersWithCuepoints,
      handleAddHighlightOnlyVideo,
      hasUploadedVideo,
      newSectionHighlight,
      projectId,
      setCurrentHighlight,
      highlightedStoryboard,
      trackEvent,
      addUpdateCount,
      setNewSectionHighlight,
      setCurrentCuepoint,
    ]
  );

  //Workaround for Area Highlights
  // PdfHighlighter isn't re-rendered on props change.
  // When we create area highlight it uses initial version
  // of handleAddHighlight function with old states
  const handleAddHighlightRef = useRef(handleAddHighlight);
  useEffect(() => {
    handleAddHighlightRef.current = handleAddHighlight;
  }, [handleAddHighlight]);

  const scrollTimelineToCuepoint = (highlightId: string) => {
    const curCuepoint = cuepoints?.find((c) => c.sourceId === highlightId);
    const videoElement = document.querySelector("video");
    const timeline = timelineRef.current;

    if (!curCuepoint || !videoElement || !timeline) return;

    const { container, player, scaleWidth } = timeline;
    const visibleContainerWidth = container?.clientWidth;

    if (!visibleContainerWidth || !player || !scaleWidth) return;

    const currentTime = videoElement.currentTime;
    const cursorPositionPx = curCuepoint.start * scaleWidth;
    const currentScrollPositionPx = currentTime * scaleWidth;

    const currentPage = Math.floor(
      currentScrollPositionPx / visibleContainerWidth
    );
    const targetPage = Math.floor(cursorPositionPx / visibleContainerWidth);

    videoElement.currentTime = curCuepoint.start;

    if (currentPage !== targetPage) {
      const newScrollLeft = Math.max(
        0,
        cursorPositionPx - visibleContainerWidth / 2
      );
      player.setScrollLeft(newScrollLeft);
    }
  };

  const handlePdfHighlightClick = (highlight: PdfHighlight) => {
    setScrollTo(highlight.id);
    setCurrentHighlight(highlight);
    setCurrentCuepoint(highlight.id);
    scrollTimelineToCuepoint(highlight.id);
  };

  const handleScroll = (id: string, effects?: boolean) => {
    const curHighlight = highlights?.find((h) => h.id === id);

    if (!effects) {
      setCurrentHighlight(curHighlight);
      setCurrentCuepoint(id);

      scrollTimelineToCuepoint(id);
    }

    setScrollTo(id);
    if (curHighlight) {
      postToIframe({
        type: "word-clicked",
        id: curHighlight.id,
      });
    } else {
      postToIframe({
        type: "cuepoint-picked",
        id,
      });
    }
  };

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectDetailRoute = getProjectDetailRoute(
    projectData?.data.id ?? "",
    projectData?.data.type,
    media?.instance_id
  );

  const handleBackClick = () => {
    if (updateCount > 0) {
      displayModalConfirm(projectData?.data.id, theme.colorPrimary, () => {
        // Since the Backbutton is a bit different than the other places where we
        // limit navigation, this as added to enable navigation on confirm.
        disableBlocking();
        go({ to: projectDetailRoute });
      });
    } else {
      go({ to: projectDetailRoute });
    }
  };

  const handleSave = async () => {
    if (updateCount === 0) {
      setSaving(true);
      setTimeout(() => {
        setSaving(false);
      }, 500);
      return;
    }

    try {
      setSaving(true);
      // Track the save event
      trackEvent(AnalyticsEvents.DocumentAnimationSaveAll, {
        project_id: projectId,
        updates_count: updateCount,
      });

      await saveUpdatedData(storyboardHighlights);

      await Promise.all([refetchMediaAssets(), refetchProjectAssets()]);
      // Show success notification
      api.success({
        message: t("components.binder.index.saveSuccessful"),
        description: t("components.binder.index.changesHaveBeen"),
        placement: "bottomRight",
      });

      setSaving(false);
      setUpdateCount(0);
      setCurrentHighlight(undefined);
      setCurrentCuepoint(undefined);
      setNewSectionHighlight(undefined);

      localStorage.setItem(STORAGE_SETTINGS_KEY, "");
    } catch (error) {
      console.error("Error saving data", error);
      // Show error notification
      api.error({
        message: t("components.binder.index.saveFailed"),
        description: t("components.binder.index.failedToSave"),
        placement: "bottomRight",
      });
    }
  };

  const pdfExtensions = [".pdf"];
  const htmlExtensions = [".html", ".htm"];

  const hasExtension = (
    filename: string | undefined,
    extensions: string[]
  ): boolean => {
    if (!filename) return false;
    const lowercaseName = filename.toLowerCase();
    return extensions.some((ext) => lowercaseName.endsWith(ext));
  };

  const isPdf = hasExtension(documentAsset?.name, pdfExtensions);
  const isHtml = !isPdf && hasExtension(documentAsset?.name, htmlExtensions);
  const prefix = isHtml ? "Web" : "";
  const pageTooltip = t(`components.binder.index.toChoose${prefix}`);

  const webPageUrl = documentAsset?.source_url ?? "";
  const pageId = useMemo(() => {
    const pageId = projectData?.data.tags.find(
      (tag) => tag.tag_key === "page_id"
    );
    return pageId ? pageId.tag_value : "";
  }, [projectData?.data.tags]);

  const componentProps = {
    document: { ...documentAsset, url: documentAsset?.path },
    startScrollTo: scrollTo,
    addHighlight: (highlight) => handleAddHighlightRef.current(highlight),
    handleOnSave: handleSave,
    onHighlightClick: handlePdfHighlightClick,
  };
  const webComponentProps = {
    ...componentProps,
    addHighlight: componentProps.addHighlight as (
      highlight: NewHtmlHighlight
    ) => Promise<{
      highlight: NewHtmlHighlight & { id: string };
      cuepoint: Cuepoint;
    }>,
  };
  const brandKitPalette = brandKit?.data?.palette;

  const components = {
    pdf: <PdfHighlightsViewer {...componentProps} />,
    html: (
      <AnimationWeb
        {...webComponentProps}
        pageUrl={webPageUrl}
        pageId={pageId}
        organizationId={organizationId}
        language={projectData?.data?.settings?.language || "en-US"}
        viewType={viewType}
        storyboardHighlights={storyboardHighlights}
        projectMedia={media}
        projectDocument={documentAsset}
        brandKitPalette={brandKitPalette}
        projectId={projectId}
      />
    ),
  };
  const component = isHtml ? components.html : components.pdf;
  const saveButtonText = () => {
    let text = t("components.binder.index.save", {
      count: updateCount,
    });

    if (saving) {
      text = t("components.binder.index.saving");
    }

    if (updateCount === 0) {
      text = t("buttons.save");
    }

    return text;
  };

  return (
    <div
      style={{
        height: "calc(100vh - 200px)",
      }}
    >
      <LoadingSpinner loading={isLoading || isLegacyDataMigrationLoading} />
      {isLegacyDataMigrationError && <NoAnimationModal projectId={projectId} />}
      {contextHolder}
      <Flex vertical>
        <LiveHintWindow
          isHtml={isHtml}
          isScriptClicked={!!newSectionHighlight}
        />
        <SubPageHeader
          itemId={projectData?.data.id}
          itemTitle={projectData?.data.title}
          page="animation"
          source="projects"
          onBack={handleBackClick}
          rightContent={
            <Space size={"small"}>
              {isHtml && (
                <Tag
                  color="default"
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                  }}
                >
                  HeyGenius
                </Tag>
              )}
              {isHtml && (
                <Tooltip
                  showArrow={false}
                  mouseEnterDelay={0.5}
                  title={
                    <a
                      href={webPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography.Text
                        copyable
                        ellipsis
                        style={{
                          fontSize: 10,
                          whiteSpace: "nowrap",
                          color: "#fff",
                        }}
                      >
                        {webPageUrl}
                      </Typography.Text>
                    </a>
                  }
                >
                  <Button
                    icon={
                      <ArrowSquareUpRightIcon
                        size={20}
                        style={{ verticalAlign: "text-top" }}
                      />
                    }
                    size="middle"
                    href={webPageUrl}
                    target="_blank"
                    type="text"
                  ></Button>
                </Tooltip>
              )}
              <Tooltip title={pageTooltip}>
                <Button
                  icon={
                    <InfoIcon size={20} style={{ verticalAlign: "text-top" }} />
                  }
                  size="middle"
                  type="text"
                ></Button>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Debug">
                  <Button
                    type="text"
                    icon={
                      <BugBeetleIcon
                        size={20}
                        style={{ verticalAlign: "text-top" }}
                      />
                    }
                    onClick={() => setDebug((value) => !value)}
                  />
                </Tooltip>
              )}
              <ColorPicker
                size="middle"
                defaultValue={currentCuepoint?.roughNotation?.color || "#000"}
                onOpenChange={(open) => {
                  if (open) {
                    trackEvent(AnalyticsEvents.DocumentAnimationModifyAll, {
                      project_id: projectId,
                    });
                  }
                }}
                onChangeComplete={(value) => {
                  modifyColorAllHighlights(value.toHexString());
                }}
                palette={brandKitPalette}
              >
                <Button type="text" icon={<EditOutlined />} size="middle">
                  {t(`components.binder.index.changeAll`)}
                </Button>
              </ColorPicker>
              <Popconfirm
                title={t(`buttons.confirm`)}
                onConfirm={() => removeAll(projectId)}
                okText={t(`components.ConfirmModal.ConfirmModal.yes`)}
                cancelText={t(`components.ConfirmModal.ConfirmModal.no`)}
                okType="danger"
              >
                <Button type="text" size="middle" icon={<DeleteOutlined />}>
                  {t(`buttons.deleteAll`)}
                </Button>
              </Popconfirm>

              {isHtml && (
                <Tooltip
                  mouseEnterDelay={0.5}
                  title={t("components.binder.index.previewTooltip")}
                >
                  <ConfigProvider
                    theme={{
                      token: {
                        // pink from pixmania logo (Florian)
                        colorPrimary: "#C50060",
                      },
                    }}
                  >
                    <TrackedButton
                      trackingEventName={
                        AnalyticsEvents.DocumentAnimationPreview
                      }
                      trackingProps={{
                        project_id: projectId,
                        project_type: projectData?.data.type as ProjectType,
                        view_type: viewType,
                      }}
                      trackingText={t("components.binder.index.preview")}
                      color="primary"
                      size="middle"
                      variant={viewType === "preview" ? "solid" : "outlined"}
                      icon={<PlayIcon weight="bold" />}
                      onClick={() => {
                        setCurrentCuepoint(undefined);
                        setCurrentHighlight(undefined);
                        setViewType((prev) =>
                          prev === "preview" ? "editor" : "preview"
                        );
                      }}
                      style={{
                        // background: "none",
                        paddingLeft: 5,
                        paddingRight: 8,
                      }}
                    >
                      <Space size="middle">
                        {t("components.binder.index.preview")}
                        {/* <Switch checked={viewType === "preview"} size="small" /> */}
                      </Space>
                    </TrackedButton>
                  </ConfigProvider>
                </Tooltip>
              )}

              <Button
                type="primary"
                size="middle"
                onClick={() => void handleSave()}
                disabled={saving}
                loading={saving}
              >
                {saveButtonText()}
              </Button>
            </Space>
          }
        />
        {isHtml && (
          <Flex
            align="center"
            justify="center"
            style={{
              background: "#E3F2FD",
              color: "#1565C0",
              fontSize: 12,
              flexGrow: 1,
              width: `calc(100% + ${pagePadding.left + pagePadding.right}px)`,
              marginLeft: -pagePadding.left,
              padding: `8px ${pagePadding.right}px 8px ${pagePadding.left}px`,
            }}
          >
            {t("src.pages.media.components.binder.visualizationWarning")}
          </Flex>
        )}

        <Flex vertical gap={10} style={{ maxHeight: "calc(100vh - 60px)" }}>
          <PanelGroup
            direction="horizontal"
            style={{ height: "calc(100vh - 200px)" }}
          >
            <Panel defaultSize={40} minSize={20}>
              <Flex
                vertical
                gap={10}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {highlightedStoryboard && !hasUploadedVideo && (
                  <Tabs
                    size="small"
                    items={[
                      ...(media
                        ? [
                            {
                              key: "1",
                              label: (
                                <span
                                  // hack to avoid parent padding
                                  style={{
                                    paddingLeft: theme.paddingContentHorizontal,
                                  }}
                                >
                                  {t(`components.binder.index.video`)}
                                </span>
                              ),
                              children: <AnimationVideo media={media} />,
                              forceRender: true,
                            },
                          ]
                        : []),
                      {
                        key: "2",
                        label: t(`components.binder.index.script`),
                        children: isScriptLoading ? (
                          <>
                            <Skeleton active />
                            <Skeleton active />
                            <Skeleton active />
                          </>
                        ) : (
                          <AnimatorScript
                            onScroll={handleScroll}
                            newSectionHighlight={newSectionHighlight}
                            setNewSectionHighlight={setNewSectionHighlight}
                            onSelectionChange={updateCuepointDuration}
                            style={layoutDirection}
                            isRTL={isRTL(storyboardLanguage)}
                            language={storyboardLanguage}
                          />
                        ),
                      },
                    ]}
                    defaultActiveKey="2"
                  />
                )}
                {hasUploadedVideo && (
                  <Flex
                    style={{ width: "100%", height: "100%", padding: 24 }}
                    vertical
                  >
                    <Flex vertical align="center">
                      <Typography.Text strong>
                        {t("components.binder.index.limitedExperience")}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {t("components.binder.index.scriptUnavailable")}
                      </Typography.Text>
                    </Flex>
                    <AnimationVideo media={media} />
                  </Flex>
                )}
              </Flex>
            </Panel>
            <PanelResizeHandle>
              <StyledVerticalPanelResizeHandle />
            </PanelResizeHandle>
            <Panel defaultSize={60} minSize={40}>
              <Flex
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                {documentAsset && component}
                {viewType === "editor" &&
                  currentHighlight &&
                  brandKitPalette && (
                    <InspectorToolbar
                      brandKitPalette={brandKitPalette}
                      projectId={projectId}
                    />
                  )}
              </Flex>
            </Panel>
          </PanelGroup>
          {(media?.url || hasUploadedVideo) && (
            <TimelineComponent
              ref={timelineRef}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleScroll={handleScroll}
              duration={duration}
              setTs={setTs}
              ts={ts / 1000}
            />
          )}
        </Flex>
      </Flex>
      <Drawer open={debug} onClose={() => setDebug(false)} size="large">
        <Flex gap={30} style={{ padding: 10 }} justify="space-between">
          <JsonPreview data={cuepoints} />
          <JsonPreview data={highlights} />
        </Flex>
      </Drawer>
    </div>
  );
};
export type ViewType = "editor" | "preview" | "standalone-preview";
